import { File, MoreVertical, Trash2 } from "lucide-react";
import type React from "react";

interface SourceDocument {
	id: string;
	title: string;
	contentPath?: string;
	createdAt?: string;
}

interface SourceListProps {
	documents: SourceDocument[];
	selectedSources: Set<string>;
	openMenuId: string | null;
	menuRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
	onSourceToggle: (sourceId: string) => void;
	onToggleMenu: (documentId: string) => void;
	onDelete: (documentId: string, event: React.MouseEvent) => void;
	deletePending: boolean;
}

const SourceList: React.FC<SourceListProps> = ({
	documents,
	selectedSources,
	openMenuId,
	menuRefs,
	onSourceToggle,
	onToggleMenu,
	onDelete,
	deletePending,
}) => {
	if (documents.length === 0) {
		return null;
	}

	return (
		<div className="p-1.5">
			{documents.map((document) => (
				<div
					key={document.id}
					className="flex items-start gap-2 px-2 py-2 rounded-lg soft-hover transition-colors group"
				>
					<label className="flex items-start gap-2 flex-1 cursor-pointer min-w-0">
						<input
							type="checkbox"
							checked={selectedSources.has(document.id)}
							onChange={() => onSourceToggle(document.id)}
							className="mt-0.5 w-3.5 h-3.5 text-primary-600 border-strong rounded focus:ring-primary-500 flex-shrink-0"
						/>
						<div className="flex-1 min-w-0 overflow-hidden">
							<div className="flex items-center gap-1.5 min-w-0">
								<File className="w-3.5 h-3.5 text-subtle flex-shrink-0" />
								<span className="text-xs font-medium text-primary truncate min-w-0 flex-1">{document.title}</span>
							</div>
							{document.createdAt && (
								<p className="text-xs text-subtle mt-0.5 truncate">
									{new Date(document.createdAt).toLocaleDateString()}
								</p>
							)}
						</div>
					</label>
					<div
						className="relative"
						ref={(element) => {
							if (element) {
								menuRefs.current.set(document.id, element);
							} else {
								menuRefs.current.delete(document.id);
							}
						}}
					>
						<button
							type="button"
							onClick={(event) => {
								event.stopPropagation();
								onToggleMenu(document.id);
							}}
							className="p-1 soft-hover rounded transition-colors opacity-0 group-hover:opacity-100"
							aria-label="Document options"
						>
							<MoreVertical className="w-3.5 h-3.5 text-muted" />
						</button>
						{openMenuId === document.id && (
							<div className="absolute right-0 top-8 z-10 surface border border-subtle rounded-lg shadow-lg min-w-[100px]">
								<button
									type="button"
									onClick={(event) => onDelete(document.id, event)}
									disabled={deletePending}
									className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
								>
									<Trash2 className="w-3.5 h-3.5" />
									<span>Delete</span>
								</button>
							</div>
						)}
					</div>
				</div>
			))}
		</div>
	);
};

export default SourceList;
